// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * Sidebar Context
 *
 * Manages user preferences for the sidebar display mode:
 * - 'full': Full width sidebar with labels (default)
 * - 'hover': Icons-only sidebar that expands on mouse hover
 *
 * Also manages sidebar visibility (open/closed) controlled by the hamburger menu.
 *
 * Preferences are persisted to localStorage (no PHI involved).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { JSX, ReactNode } from 'react';

const STORAGE_KEY = 'carepoint-sidebar-mode';

export type SidebarMode = 'full' | 'hover';

interface SidebarContextValue {
  /** Current sidebar mode (saved preference) */
  mode: SidebarMode;
  /** Whether the sidebar is visible (controlled by hamburger) */
  isOpen: boolean;
  /** Whether the sidebar is currently expanded (for hover mode - expands on mouse over) */
  isExpanded: boolean;
  /** Set the sidebar mode */
  setMode: (mode: SidebarMode) => void;
  /** Toggle sidebar visibility (hamburger) */
  toggleOpen: () => void;
  /** Set sidebar visibility */
  setIsOpen: (open: boolean) => void;
  /** Handlers for mouse enter/leave (for hover mode) */
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
}

const SidebarContext = createContext<SidebarContextValue | undefined>(undefined);

interface SidebarProviderProps {
  children: ReactNode;
}

export function SidebarProvider({ children }: SidebarProviderProps): JSX.Element {
  const [mode, setModeState] = useState<SidebarMode>('full');
  const [isOpen, setIsOpen] = useState(true); // Sidebar visibility (hamburger)
  const [isExpanded, setIsExpanded] = useState(true); // For hover mode expansion

  // Load preference from localStorage on mount
  useEffect(() => {
    const savedMode = localStorage.getItem(STORAGE_KEY) as SidebarMode | null;
    if (savedMode === 'full' || savedMode === 'hover') {
      setModeState(savedMode);
      // In hover mode, start collapsed (icons only)
      if (savedMode === 'hover') {
        setIsExpanded(false);
      }
    }
  }, []);

  // Save preference to localStorage
  const setMode = useCallback((newMode: SidebarMode): void => {
    setModeState(newMode);
    localStorage.setItem(STORAGE_KEY, newMode);
    // Adjust expanded state based on mode
    if (newMode === 'full') {
      setIsExpanded(true);
    } else {
      setIsExpanded(false);
    }
  }, []);

  // Toggle sidebar visibility (hamburger)
  const toggleOpen = useCallback((): void => {
    setIsOpen((prev) => !prev);
  }, []);

  // Mouse handlers for hover mode
  const handleMouseEnter = useCallback((): void => {
    if (mode === 'hover') {
      setIsExpanded(true);
    }
  }, [mode]);

  const handleMouseLeave = useCallback((): void => {
    if (mode === 'hover') {
      setIsExpanded(false);
    }
  }, [mode]);

  const value = useMemo(
    (): SidebarContextValue => ({
      mode,
      isOpen,
      isExpanded,
      setMode,
      toggleOpen,
      setIsOpen,
      handleMouseEnter,
      handleMouseLeave,
    }),
    [mode, isOpen, isExpanded, setMode, toggleOpen, handleMouseEnter, handleMouseLeave]
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

export function useSidebar(): SidebarContextValue {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}
